import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "../../../../configs/apiRoutes";
import instance from "../../../../api/apiClient";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface LoyaltyPointsItem {
  id: number;
  type: string;
  currency: string;

  country?: string;

  min?: number;
  max?: number;

  numberOfStep?: number;
  numberOfDeposit?: number;

  wagerTitle?: string;
  wagerGameListId?: string;

  points?: number;

  multiplier?: number;
  useDepositAmount?: boolean;
  depositMultiplier?: number | null;
  scope?: string;
  gameType?: string | null;
  providerId?: number | null;
  gameIds?: number[];
  loyaltyLevelId?: number | null;
  loyaltyMultiplier?: number | null;
  isActive?: number;
  isRealBalanceAllowed?: number;
  isBonusBalanceAllowed?: number;
}

interface LoyaltyPointsState {
  list: LoyaltyPointsItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: number[];
  payload?: Partial<LoyaltyPointsItem>;
  processingItemId?: LoyaltyPointsItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  geos?: string[];
  currencies?: string[];
  numberOfDeposit?: string;
  numberOfStep?: string;
  type: "bet" | "deposit" | "signUp" | "wager";
  order: {
    id: string;
    currency?: string;
    country?: string;
    points?: string;
    numberOfDeposit?: string;
    numberOfStep?: string;
  };
}

export const getLoyaltyPointsListList = createAsyncThunk(
  "loyalty-points/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.loyaltyPoints}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getLoyaltyPointsItem = createAsyncThunk(
  "loyalty-points/item",
  async (data: { id: string; type: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.loyaltyPointsItem}/${data.id}?type=${data.type}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createLoyaltyPoints = createAsyncThunk<
  boolean,
  LoyaltyPointsItem,
  { rejectValue: ApiErrorPayload }
>(
  "loyalty-points/create",
  async (data: LoyaltyPointsItem, { rejectWithValue }) => {
    try {
      await instance.post(`${apiRoutes.loyaltyPointsCreate}`, data);
      return true;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateLoyaltyPoints = createAsyncThunk<
  boolean,
  LoyaltyPointsItem,
  { rejectValue: ApiErrorPayload }
>("loyalty-points/update", async (data: LoyaltyPointsItem, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.loyaltyPointsUpdate}/${data.id}`, data);
    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const deleteLoyaltyPoints = createAsyncThunk(
    "loyalty-points/delete",
    async (data: {id: string | number, type: string}) => {
      try {
        await instance.delete(`${apiRoutes.loyaltyPointsDelete}/${data.id}`, {
          data: { type: data.type },
        });
        return true;
      } catch (error) {
        return false;
      }
    },
);

const initialState: LoyaltyPointsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
};

export const loyaltyPointsSlice = createSlice({
  name: "loyalty-points",
  initialState,
  reducers: {
    handlePaginate: (
      state: LoyaltyPointsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClear: (state: LoyaltyPointsState) => {
      state.activePage = 1;
      state.isListFetched = false;
      state.selectedItems = [];
      state.list = [];
      state.totalItems = 1;
      state.totalPages = 1;
    },
    handleClearFields: (state: LoyaltyPointsState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handleUpdateFields: (
      state: LoyaltyPointsState,
      action: PayloadAction<Partial<LoyaltyPointsItem>>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleSelectItem: (
      state: LoyaltyPointsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: LoyaltyPointsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLoyaltyPointsListList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getLoyaltyPointsItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = {
        ...action.payload,
        wagerTitle: action.payload.wagerTitle
          ? parseInt(action.payload.wagerTitle, 10)
          : undefined,
        wagerGameListId: action.payload.wagerTitle
          ? parseInt(action.payload.wagerTitle, 10)
          : undefined,
      };
    });
    builder.addCase(createLoyaltyPoints.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createLoyaltyPoints.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createLoyaltyPoints.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(deleteLoyaltyPoints.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteLoyaltyPoints.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteLoyaltyPoints.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updateLoyaltyPoints.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateLoyaltyPoints.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateLoyaltyPoints.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleClear,
  handleClearFields,
  handleUpdateFields,
} = loyaltyPointsSlice.actions;
